import React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@v2/components/ui/command';
import { TagsInputProps } from '@v2/components/form/TagsInput/TagsInput.types';
import { mergeRefs } from '@gettactic/helpers/src';
import { Chip } from '@v2/components/display/chips/Chip/Chip';

export const TagsInput = React.forwardRef<HTMLInputElement, TagsInputProps>(
  ({ selectedTags: controlledValue, onChangeValue, ...props }, ref) => {
    const [searchInputValue, setSearchInputValue] = React.useState('');
    const { tags, noItemsFoundMessage = 'No items left', ...rest } = props;
    const [uncontrolledSelectedItems, setSelectedItems] = React.useState<
      string[]
    >([]);
    const [wrapperRef, setWrapperRef] = React.useState<HTMLDivElement | null>(
      null
    );
    const [minWidth, setMinWidth] = React.useState<number>(0);
    const [isOpen, setIsOpen] = React.useState(false);

    const selectedItems =
      controlledValue !== undefined
        ? controlledValue
        : uncontrolledSelectedItems;

    React.useEffect(() => {
      if (wrapperRef) {
        const { width } = wrapperRef.getBoundingClientRect();
        setMinWidth(width);
      }
    }, [wrapperRef, isOpen]);

    const availableTags = tags.filter((tag) => {
      return !selectedItems.includes(tag.value);
    });

    const handleChange = (items: string[]) => {
      if (controlledValue === undefined) {
        setSelectedItems(items);
      }
      // reset input value
      setSearchInputValue('');
      onChangeValue?.(items);
    };

    const handleInputValueChange = (value: string) => {
      setSearchInputValue(value);
    };

    return (
      <div>
        <Command className={'p-0'} asPopover onPopoverOpenChange={setIsOpen}>
          <CommandInput
            wrapperRef={setWrapperRef}
            {...rest}
            value={searchInputValue}
            onValueChange={handleInputValueChange}
          />
          <CommandList
            popoverContentProps={{
              showArrow: false,
              className: 'p-0',
              style: minWidth
                ? {
                    minWidth: minWidth
                  }
                : undefined
            }}
          >
            <CommandEmpty>{noItemsFoundMessage}</CommandEmpty>
            <CommandGroup>
              {(availableTags || []).map((tag) => {
                return (
                  <CommandItem
                    ref={mergeRefs([ref])}
                    key={tag.value}
                    className={'rounded-[3px] p-0 px-2'}
                    keywords={tag.keywords}
                    onSelect={() => {
                      let value = [...selectedItems];
                      if (value.includes(tag.value)) {
                        value = value.filter((item) => item !== tag.value);
                      } else {
                        value.push(tag.value);
                      }

                      handleChange(value);
                    }}
                  >
                    {tag.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className={'mx-[-2px] mt-1 flex w-full flex-wrap'}>
          {selectedItems.map((item) => {
            const tag = tags.find((tag) => tag.value === item);
            if (!tag) return null;

            const onCancel = () => {
              const value = [...selectedItems].filter(
                (selectedItem) => selectedItem !== item
              );
              handleChange(value);
            };

            const chipProps = {
              className: 'm-1',
              onCancel,
              onClick: onCancel
            };

            return tag.chip ? (
              <React.Fragment key={tag.value}>
                {tag.chip(chipProps)}
              </React.Fragment>
            ) : (
              <Chip key={tag.value} {...chipProps}>
                {tag?.label}
              </Chip>
            );
          })}
        </div>
      </div>
    );
  }
);

TagsInput.displayName = 'TagsInput';
